import * as arrow from "apache-arrow";
import React from "react";
import { DatePivotParams } from "../../entities/date_pivot_params";
import dayjs from "../../utils/dayjs";
import { Box, IconButton } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";

// Measures that should default to ascending sort (lower is better)
const ASC_DEFAULT_MEASURES = ["starting inventory", "ending inventory"];

interface RowData {
  type: "group" | "detail";
  groupKey?: string; // segmentationKey for group rows
  measure?: string; // measure name for detail rows
  dateValues: Record<string, any>;
  segmentationRank: number;
  measureRank?: number;
  isExpanded?: boolean;
  parentSegmentation?: string;
  groupSize?: number; // Added for group rows
  // Export compatibility properties
  segmentation?: string;
  [key: string]: any; // Allow dynamic keys for direct column access
}

interface MatrixData {
  columns: {
    key: string;
    label?: string;
    width?: number;
    isExpandColumn?: boolean;
    sortable?: boolean;
    onSort?: (() => void) | undefined;
    sticky?: boolean;
    renderCell?: (row: RowData, rowIndex: number) => React.ReactNode;
  }[];
  getRow: (rowIndex: number) => RowData;
  rowCount: number;
  arrowTable: arrow.Table;
  columnTotals?: Record<string, any>;
  segmentationIndex?: Map<
    string,
    {
      segmentationKey: string;
      segmentValues: string[];
      segmentationRank: number;
      selectedMeasureData: Map<number, any>;
      allMeasures: Map<
        string,
        {
          measure: string;
          measureRank: number;
          dateValues: Map<number, any>;
        }
      >;
    }
  >;
}

interface SegmentationInfo {
  segmentationKey: string;
  segmentValues: string[];
  segmentationRank: number;
  measureCount: number;
  startRowIndex: number;
}

export const createInventoryHealthMRPMatrix = (args: {
  arrowTable: arrow.Table;
  columns: arrow.Field<any>[];
  viewParams: DatePivotParams;
  expandedGroups: Set<string>;
  sortConfig: Array<{ key: string; direction: "asc" | "desc"; index: number }>;
  setSortConfig: React.Dispatch<
    React.SetStateAction<
      Array<{ key: string; direction: "asc" | "desc"; index: number }>
    >
  >;
  setExpandedGroups: React.Dispatch<React.SetStateAction<Set<string>>>;
  theme: any;
}): MatrixData => {
  const {
    arrowTable,
    columns,
    viewParams,
    expandedGroups,
    sortConfig,
    setSortConfig,
    setExpandedGroups,
  } = args;

  if (
    !arrowTable ||
    !columns ||
    !viewParams?.segments ||
    !viewParams?.dateHierarchy
  ) {
    // Return empty table data instead of null
    return {
      columns: [],
      getRow: () => ({ type: "detail", dateValues: {}, segmentationRank: 0 }),
      rowCount: 0,
      arrowTable: arrow.tableFromArrays({}),
      columnTotals: undefined,
    };
  }

  // Get unique dates efficiently using Arrow vectors directly
  const dateField = viewParams.dateHierarchy;
  const dateVector = arrowTable.getChild(dateField);
  if (!dateVector) {
    // Return empty table data instead of null
    return {
      columns: [],
      getRow: () => ({ type: "detail", dateValues: {}, segmentationRank: 0 }),
      rowCount: 0,
      arrowTable: arrow.tableFromArrays({}),
      columnTotals: undefined,
    };
  }

  const dateSet = new Set<number>();
  for (let i = 0; i < dateVector.length; i++) {
    const dateValue = dateVector.get(i);
    if (dateValue != null) {
      dateSet.add(dayjs.utc(dateValue).valueOf());
    }
  }
  const allDates = Array.from(dateSet)
    .sort((a, b) => a - b)
    .map((ts) => dayjs.utc(ts));

  // Safety check: ensure we have valid dates
  if (allDates.length === 0) {
    // Return empty table data instead of null
    return {
      columns: [],
      getRow: () => ({ type: "detail", dateValues: {}, segmentationRank: 0 }),
      rowCount: 0,
      arrowTable: arrow.tableFromArrays({}),
      columnTotals: undefined,
    };
  }
  // If no explicit sort config is provided, create default sort for all dates
  if (sortConfig.length === 0) {
    // Determine default direction based on measure type
    const defaultDirection: "asc" | "desc" =
      viewParams.measure &&
      ASC_DEFAULT_MEASURES.some(
        (measure) => viewParams.measure?.toLowerCase() === measure.toLowerCase()
      )
        ? "asc" // Lower values are better for these measures
        : "desc"; // Higher values are better for other measures

    const initialSort = allDates.map((date, index) => ({
      key: `${date.valueOf()}`,
      direction: defaultDirection,
      index,
    }));

    setSortConfig(initialSort);

    return {
      columns: [],
      getRow: () => ({ type: "detail", dateValues: {}, segmentationRank: 0 }),
      rowCount: 0,
      arrowTable: arrow.tableFromArrays({}),
      columnTotals: undefined,
    };
  }

  // Get all necessary vectors once
  const segmentVectors = viewParams.segments.map((segment) =>
    arrowTable.getChild(segment)
  );
  const measureVector = arrowTable.getChild("measure");
  const valueVector = arrowTable.getChild("value");
  const segmentationRankVector =
    arrowTable.getChild("segmentation_rank") ||
    arrowTable.getChild("segmentationRank");
  const measureRankVector =
    arrowTable.getChild("measure_rank") || arrowTable.getChild("metric_rank");

  if (!measureVector || !valueVector || segmentVectors.some((v) => !v)) {
    // Return empty table data instead of null
    return {
      columns: [],
      getRow: () => ({ type: "detail", dateValues: {}, segmentationRank: 0 }),
      rowCount: 0,
      arrowTable: arrow.tableFromArrays({}),
      columnTotals: undefined,
    };
  }

  // Build segmentation index (keep Arrow data, just organize metadata)
  const segmentationIndex = new Map<
    string,
    {
      segmentationKey: string;
      segmentValues: string[];
      segmentationRank: number;
      selectedMeasureData: Map<number, any>; // dateValue -> value for selected measure
      allMeasures: Map<
        string,
        {
          measure: string;
          measureRank: number;
          dateValues: Map<number, any>; // dateValue -> value
        }
      >;
    }
  >();

  // Single pass through Arrow data - build index without converting to arrays
  for (let i = 0; i < arrowTable.numRows; i++) {
    const segmentValues = segmentVectors.map((vector) =>
      String(vector!.get(i) || "")
    );
    const segmentationKey = segmentValues.join(" | ");
    const measure = String(measureVector.get(i) || "");
    const dateValue = dayjs.utc(dateVector.get(i)).valueOf();
    const value = valueVector.get(i);
    const segmentationRank = Number(segmentationRankVector?.get(i) || 0);
    const measureRank = Number(measureRankVector?.get(i) || 0);

    // Initialize segmentation if not exists
    if (!segmentationIndex.has(segmentationKey)) {
      segmentationIndex.set(segmentationKey, {
        segmentationKey,
        segmentValues,
        segmentationRank,
        selectedMeasureData: new Map(),
        allMeasures: new Map(),
      });
    }

    const segData = segmentationIndex.get(segmentationKey)!;

    // Store data for selected measure (or all measures if measure is null)
    if (
      !viewParams.measure ||
      measure.toLowerCase() === viewParams.measure.toLowerCase()
    ) {
      segData.selectedMeasureData.set(dateValue, value);
    }

    // Store all measures
    if (!segData.allMeasures.has(measure)) {
      segData.allMeasures.set(measure, {
        measure,
        measureRank,
        dateValues: new Map(),
      });
    }
    segData.allMeasures.get(measure)!.dateValues.set(dateValue, value);
  }

  const sortedSegmentations = Array.from(segmentationIndex.values()).sort(
    (a, b) => {
      let comparison = 0;

      // Apply multi-column date sorting in order of precedence
      for (const sort of sortConfig) {
        // Check if this is a date column by trying to parse the key as a timestamp
        const dateValue = parseInt(sort.key);
        if (!isNaN(dateValue) && dateValue > 1000000000000) {
          // Valid timestamp check
          const aValue = a.selectedMeasureData.get(dateValue) ?? 0;
          const bValue = b.selectedMeasureData.get(dateValue) ?? 0;

          // Convert to numbers, handling formatted strings with commas
          const aNum =
            typeof aValue === "number"
              ? aValue
              : typeof aValue === "string"
                ? parseFloat(aValue.replace(/,/g, "")) || 0
                : Number(aValue) || 0;
          const bNum =
            typeof bValue === "number"
              ? bValue
              : typeof bValue === "string"
                ? parseFloat(bValue.replace(/,/g, "")) || 0
                : Number(bValue) || 0;

          // Sort by absolute values (negative inventory should sort by magnitude)
          comparison = Math.abs(aNum) - Math.abs(bNum);

          if (comparison !== 0) {
            return sort.direction === "desc" ? -comparison : comparison;
          }
        }
      }

      // If all date values are equal, maintain original order
      return 0;
    }
  );

  // Calculate row counts and positions without building full arrays
  const segmentationInfos: SegmentationInfo[] = [];
  let totalRowCount = 0;

  sortedSegmentations.forEach((segData) => {
    const isExpanded =
      expandedGroups.has("*") || expandedGroups.has(segData.segmentationKey);
    const measureCount = isExpanded ? segData.allMeasures.size : 0;

    segmentationInfos.push({
      segmentationKey: segData.segmentationKey,
      segmentValues: segData.segmentValues,
      segmentationRank: segData.segmentationRank,
      measureCount,
      startRowIndex: totalRowCount,
    });

    totalRowCount += 1 + measureCount; // 1 for segmentation row + measure rows
  });

  const handleDateColumnSort = (e?: React.MouseEvent<HTMLTableCellElement>) => {
    if (!setSortConfig) return;

    // Get all date columns using the timestamp values directly
    const dateColumns = allDates.map((date) => `${date.valueOf()}`);

    // Check current state - if any date column is explicitly sorted asc, we're in asc mode
    const hasAscSort = sortConfig?.some((s) => s.direction === "asc");
    const hasDescSort = sortConfig?.some((s) => s.direction === "desc");

    // Determine the new direction based on current state and measure type
    let newDirection: "asc" | "desc";

    if (!hasAscSort && !hasDescSort) {
      // No sort applied yet - determine what the initial direction should be
      const isInventoryMeasure =
        viewParams.measure &&
        ASC_DEFAULT_MEASURES.some(
          (measure) =>
            viewParams.measure?.toLowerCase() === measure.toLowerCase()
        );
      const initialDirection = isInventoryMeasure ? "asc" : "desc";
      // Toggle to the opposite of the initial direction
      newDirection = initialDirection === "asc" ? "desc" : "asc";
    } else if (hasAscSort) {
      // Currently ascending - toggle to descending
      newDirection = "desc";
    } else {
      // Currently descending - toggle to ascending
      newDirection = "asc";
    }

    // Create sort config for all date columns with earlier dates having higher precedence
    const newSortConfig = dateColumns.map((col, index) => ({
      key: col,
      direction: newDirection,
      index,
    }));

    setSortConfig(newSortConfig);
  };

  // Create MUI-compatible columns with header content
  const muiColumns = [
    {
      key: "expand",
      label: "",
      width: 40,
      isExpandColumn: true,
      sortable: false,
      sticky: true,
      renderCell: (row: RowData, rowIndex: number) => {
        const isGroup = row.type === "group";
        return isGroup
          ? React.createElement(Box, {
              sx: {
                width: "100%",
                height: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                p: 0,
              },
              children: React.createElement(IconButton, {
                size: "small",
                onClick: () =>
                  setExpandedGroups((prev) => {
                    const newSet = new Set(prev);
                    if (newSet.has(row.groupKey!)) {
                      newSet.delete(row.groupKey!);
                    } else {
                      newSet.add(row.groupKey!);
                    }
                    return newSet;
                  }),
                sx: {
                  p: 0,
                },
                children: row.isExpanded
                  ? React.createElement(ExpandLessIcon)
                  : React.createElement(ExpandMoreIcon),
              }),
            })
          : null;
      },
    },
    {
      key: "segmentation",
      label: "segmentation / measure",
      width: 200,
      sortable: false,
      sticky: true,
      renderCell: (row: RowData, rowIndex: number) => {
        const isGroup = row.type === "group";
        const isDetail = row.type === "detail";
        return React.createElement(Box, {
          sx: {
            width: "100%",
            height: "100%",
            display: "flex",
            alignItems: "center",
            fontWeight: isGroup ? "bold" : "normal",
            backgroundColor: isDetail ? "action.hover" : undefined,
            paddingLeft: isDetail ? 2 : 1, // Small indent for detail rows
          },
          children: isGroup ? row.groupKey : row.measure,
        });
      },
    },
    ...allDates.map((date, index) => ({
      key: `${date.valueOf()}`,
      width: 120,
      sortable: true,
      onSort: handleDateColumnSort,
      headerType: new arrow.Timestamp(arrow.TimeUnit.MILLISECOND),
      renderCell: (row: RowData, rowIndex: number) => {
        const dateValue = parseInt(date.valueOf().toString());
        const value = row.dateValues[dateValue];
        const isDetail = row.type === "detail";

        // Format the value for display
        let formattedValue = "";
        if (value !== null && value !== undefined && value !== "") {
          const numValue =
            typeof value === "string" ? parseFloat(value) : value;
          if (!isNaN(numValue)) {
            // Round to closest whole number and format with thousands separators
            const roundedValue = Math.round(numValue);
            formattedValue = roundedValue.toLocaleString();
          } else {
            formattedValue = String(value);
          }
        }

        return React.createElement(Box, {
          sx: {
            width: "100%",
            height: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            textAlign: "center",
            backgroundColor: isDetail ? "action.hover" : undefined,
            p: 0,
          },
          children: formattedValue,
        });
      },
    })),
  ];

  // Lazy row access function - only convert to JS when requested
  const getRow = (rowIndex: number): RowData => {
    if (rowIndex >= totalRowCount)
      return { type: "detail", dateValues: {}, segmentationRank: 0 };

    // Find which segmentation this row belongs to
    let segmentationInfo: SegmentationInfo | null = null;
    let localRowIndex = 0;

    for (const info of segmentationInfos) {
      const segmentationEndIndex = info.startRowIndex + 1 + info.measureCount;
      if (rowIndex >= info.startRowIndex && rowIndex < segmentationEndIndex) {
        segmentationInfo = info;
        localRowIndex = rowIndex - info.startRowIndex;
        break;
      }
    }

    if (!segmentationInfo)
      return { type: "detail", dateValues: {}, segmentationRank: 0 };

    const segData = segmentationIndex.get(segmentationInfo.segmentationKey)!;

    // Row 0 is always the segmentation row
    if (localRowIndex === 0) {
      // Convert Maps to Record only when requested
      const dateValues: Record<string, any> = {};
      segData.selectedMeasureData.forEach((value, dateValue) => {
        dateValues[dateValue] = value;
      });

      const rowData: RowData = {
        type: "group" as const,
        groupKey: segmentationInfo.segmentationKey,
        dateValues,
        segmentationRank: segmentationInfo.segmentationRank,
        isExpanded:
          expandedGroups.has("*") ||
          expandedGroups.has(segmentationInfo.segmentationKey),
        groupSize: segData.allMeasures.size,
      };

      // Add export compatibility properties - direct column access
      rowData.segmentation = segmentationInfo.segmentationKey;
      allDates.forEach((date) => {
        const dateKey = `${date.valueOf()}`;
        rowData[dateKey] = dateValues[date.valueOf()] || "";
      });

      return rowData;
    }

    // Otherwise it's a measure row
    const measureIndex = localRowIndex - 1;

    // Sort measures by rank to get consistent ordering
    const sortedMeasures = Array.from(segData.allMeasures.values()).sort(
      (a, b) => a.measureRank - b.measureRank
    );

    if (measureIndex >= sortedMeasures.length)
      return { type: "detail", dateValues: {}, segmentationRank: 0 };
    const targetMeasure = sortedMeasures[measureIndex];

    // Convert Maps to Record only when requested
    const dateValues: Record<string, any> = {};
    targetMeasure.dateValues.forEach((value, dateValue) => {
      dateValues[dateValue] = value;
    });

    const rowData: RowData = {
      type: "detail" as const,
      groupKey: segmentationInfo.segmentationKey,
      measure: targetMeasure.measure,
      dateValues,
      segmentationRank: segmentationInfo.segmentationRank,
      measureRank: targetMeasure.measureRank,
      parentSegmentation: segmentationInfo.segmentationKey,
    };

    // Add export compatibility properties - direct column access
    rowData.segmentation = targetMeasure.measure; // Changed from "" to targetMeasure.measure
    rowData.measure = targetMeasure.measure;
    allDates.forEach((date) => {
      const dateKey = `${date.valueOf()}`;
      rowData[dateKey] = dateValues[date.valueOf()] || "";
    });

    return rowData;
  };

  // We don't actually need an Arrow table for virtualization - TableVirtuoso only uses getRow()
  // Return a minimal empty table just to satisfy the interface
  const emptyArrowTable = arrow.tableFromArrays({
    rowCount: [totalRowCount],
  });

  return {
    columns: muiColumns,
    getRow,
    rowCount: totalRowCount,
    arrowTable: emptyArrowTable,
    columnTotals: undefined,
    segmentationIndex,
  };
};
